import { Button, TextInput, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";

import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import Head from "../../components/common/Head";
import VerticalSeriesTable from "./VerticalSeriesTable";
import useValidate from "../../hooks/useValidate";
import { getAllVerticalSeries } from "../../utils/api";
import useStore from "../../utils/store";



const VerticalSeries = () => {
  const user = useStore((state) => state.user);
  const [series, setSeries] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchText, setSearchText] = React.useState(
    searchParams.get("search") || ""
  );
  const [inputText, setInputText] = React.useState(
    searchParams.get("search") || ""
  );

  const [pagination, setPagination] = React.useState({
    current: parseInt(searchParams.get("page") || "1"),
    pageSize: 10,
  });

  useValidate();

  const getAllVerticalSeriesData = async () => {
    setLoading(true);
    const [success, response] = await getAllVerticalSeries(
      pagination.current,
      pagination.pageSize,
      searchText
    );
    if (success) setSeries(response);
    setLoading(false);
  };

  const handlePaginationChange = (page, pageSize) => {
    setPagination({ current: page, pageSize: pageSize || pagination.pageSize });
    setSearchParams({
      page: page.toString(),
      ...(searchText && { search: searchText }),
    });
  };

  const handleSearch = () => {
    setSearchText(inputText);
    setPagination({ current: 1, pageSize: 10 });
    setSearchParams({
      page: "1",
      ...(inputText && { search: inputText }),
    });
  };

  const handleSearchClear = () => {
    setInputText("");
    setSearchText("");
    setPagination({ current: 1, pageSize: 10 });
    setSearchParams({
      page: "1",
    });
  };

  React.useEffect(() => {
    getAllVerticalSeriesData();
  }, [pagination.current, pagination.pageSize, searchText]);

  const hasPermission = user?.getAllVerticalSeries !== false;

  const getPermissions = React.useMemo(() => {
    return {
      updateVerticalSeries: user?.updateVerticalSeries !== false,
      deleteVerticalSeries: user?.deleteVerticalSeries !== false,
      isPublishAccess: user?.isPublishAccess || false,
      addVerticalSeries: user?.addVerticalSeries !== false,
    };
  }, [user]);

  return (
    <div className="flex flex-col gap-4">
      
      <Head
        title="Short Drama"
        customButton={
          getPermissions.addVerticalSeries ? (
            <Link to="/vertical-series/add">
              <Button>Add Short Drama</Button>
            </Link>
          ) : null
        }
      />

      {/* Search Input */}
      <div className="flex justify-start mb-4">
        <TextInput
          placeholder="Search Short Drama by name, description..."
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          style={{ width: 400 }}
        />
        <Button onClick={handleSearch} ml="sm">Search</Button>
        <Button variant="subtle" onClick={handleSearchClear} ml="xs">Clear</Button>
      </div>

      {!hasPermission ? (
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <Text c="dimmed">
            You don't have permission to view Short Drama
          </Text>
        </div>
      ) : (
        <VerticalSeriesTable
          data={series?.data}
          loading={loading}
          pagination={{
            total: series?.metadata?.totalData,
            current: pagination.current,
            pageSize: pagination.pageSize,
            onChange: handlePaginationChange,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          reload={getAllVerticalSeriesData}
          permissions={getPermissions}
          currentPage={pagination.current}
          exportCsvData={series?.data}
          exportCsvFilename={`short-drama-${new Date().toISOString().slice(0, 10)}.csv`}
        />
      )}
    </div>
  );
};

export default VerticalSeries;
